// Restricts free-text registration input to characters Ischebeck staff can
// actually read and process. The rule removes only *letters of non-Latin
// scripts* (Arabic/Persian, Chinese and other CJK, Cyrillic, Greek, Hebrew,
// …) and *non-ASCII decimal digits* (e.g. Persian-Indic ۰۱۲). Everything
// else is kept: Latin letters incl. European diacritics (ä, ö, ü, ß, ñ, é,
// ç, Ł …), ASCII digits, whitespace, and all punctuation/symbols/typography
// such as the en dash (–), typographic apostrophe (’) or superscript (²)
// that legitimately appear in real company names, addresses and phone
// numbers.
//
// Because the check is script-based it also catches homoglyphs — e.g. a
// Greek capital Zeta "Ζ" (U+0396) typed instead of a Latin "Z" — which look
// identical but are the wrong script.

// A character is disallowed if it is a letter or decimal digit that is
// neither Latin script nor an ASCII digit.
function keepChar(ch) {
  if (!/[\p{L}\p{Nd}]/u.test(ch)) return true; // punctuation/symbols/space
  return /\p{Script=Latin}/u.test(ch) || /[0-9]/.test(ch);
}

// Removes every disallowed character from a value. Non-string input is
// returned unchanged. Iterates by code point so astral-plane characters
// (e.g. CJK extensions, emoji) are handled correctly.
export function stripDisallowedChars(value) {
  if (typeof value !== "string") return value;
  let result = "";
  for (const ch of value) {
    if (keepChar(ch)) result += ch;
  }
  return result;
}

// True if the value contains at least one disallowed character.
export function hasDisallowedChars(value) {
  if (typeof value !== "string") return false;
  for (const ch of value) {
    if (!keepChar(ch)) return true;
  }
  return false;
}

// Returns the distinct disallowed characters found in the value, in order of
// first appearance. Used to tell the user exactly which characters were
// rejected. Empty array if the value is clean or not a string.
export function getDisallowedChars(value) {
  if (typeof value !== "string") return [];
  const found = new Set();
  for (const ch of value) {
    if (!keepChar(ch)) found.add(ch);
  }
  return [...found];
}

// Collapses runs of whitespace to a single space and removes leading
// whitespace, without stripping a lone trailing space (so the user can still
// type a separator before the next word). Meant to run on every keystroke
// right after stripDisallowedChars: removing a non-Latin letter from between
// two spaces would otherwise leave a double space, and a name that starts with
// a non-Latin letter would leave a leading space behind.
export function collapseWhitespace(value) {
  if (typeof value !== "string") return value;
  return value.replace(/\s+/g, " ").replace(/^\s/, "");
}

// Formats rejected characters for display in a hint, e.g. „م“, „ر“.
export function formatBlockedChars(chars) {
  return chars.map((c) => `„${c}“`).join(", ");
}
